import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useCatalog } from "@/lib/catalog";
import { Reveal } from "@/components/Reveal";
import { DynIcon } from "@/components/DynIcon";
import { Eyebrow } from "@/components/Ui";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { TrustChecks, BenefitsBand } from "@/components/Commercial";

function Hero() {
  const { t } = useLang();
  const { catalog } = useCatalog();
  const moduleCount = catalog?.modules?.length;
  const sectorCount = catalog?.sectors?.length;
  const trial = catalog?.plans?.find((p) => p.trial_days)?.trial_days;

  return (
    <section className="bg-grid" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
        <div>
          <Reveal>
            <Eyebrow>{t("home.heroEyebrow")}</Eyebrow>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-marine sm:text-5xl lg:text-6xl">
              {t("home.heroA")}
              <br />
              <span className="text-gold">{t("home.heroB")}</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-500 md:text-lg">{t("home.heroSub")}</p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demo" className="btn-primary px-6 py-3.5 text-base shadow-lg shadow-brand/25" data-testid="hero-demo-button">
                {t("home.ctaDemo")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/modules" className="btn-outline px-6 py-3.5 text-base" data-testid="hero-modules-button">
                {t("home.ctaModules")}
              </Link>
            </div>
            <div className="mt-6">
              <TrustChecks testid="hero-trust-checks" />
            </div>
          </Reveal>
          {(moduleCount || sectorCount) && (
            <Reveal delay={400}>
              <dl className="mt-10 flex flex-wrap gap-8" data-testid="hero-stats">
                {moduleCount != null && (
                  <div>
                    <dt className="sr-only">{t("home.statModules")}</dt>
                    <dd className="font-display text-3xl font-extrabold text-marine" data-testid="stat-modules-count">
                      {moduleCount}
                    </dd>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("home.statModules")}</p>
                  </div>
                )}
                {sectorCount != null && (
                  <div>
                    <dd className="font-display text-3xl font-extrabold text-marine" data-testid="stat-sectors-count">
                      {sectorCount}
                    </dd>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("home.statSectors")}</p>
                  </div>
                )}
                {trial != null && (
                  <div>
                    <dd className="font-display text-3xl font-extrabold text-marine" data-testid="stat-trial-count">
                      {trial}
                    </dd>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("home.statTrial")}</p>
                  </div>
                )}
              </dl>
            </Reveal>
          )}
        </div>
        <Reveal delay={250} className="relative min-w-0">
          <div className="absolute -inset-3 rounded-3xl border-2 border-gold/40" aria-hidden="true" />
          <img
            src="/brand/hero.jpg"
            alt="MANAGONE"
            className="relative w-full rounded-2xl object-cover shadow-2xl shadow-marine/20"
            data-testid="hero-image"
          />
          {moduleCount != null && (
            <div
              className="absolute bottom-4 left-4 flex max-w-[calc(100%-2rem)] items-center gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-xl shadow-marine/10 backdrop-blur"
              data-testid="hero-badge"
            >
              <span className="font-display text-2xl font-extrabold text-brand">{moduleCount}</span>
              <span className="text-xs font-semibold leading-snug text-marine">{t("cta.heroBadge")}</span>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Pillars() {
  const { t } = useLang();
  const pillars = t("home.pillars");
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="pillars-section">
      <Reveal className="mx-auto max-w-2xl text-center">
        <Eyebrow>{t("home.pillarsEyebrow")}</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-bold text-marine sm:text-4xl">{t("home.pillarsTitle")}</h2>
        <p className="mt-3 text-slate-500">{t("home.pillarsSub")}</p>
      </Reveal>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {pillars.map((p, i) => (
          <Reveal key={p.title} delay={i * 100}>
            <div className="mg-card group h-full p-6 transition-shadow hover:shadow-lg hover:shadow-marine/5" data-testid={`pillar-${i}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-marine text-gold transition-colors group-hover:bg-brand group-hover:text-white">
                <DynIcon name={p.icon} className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-marine">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function SectorExplorer() {
  const { t, lang } = useLang();
  const { catalog } = useCatalog();
  const sectors = catalog?.sectors || [];
  const modules = catalog?.modules || [];
  const [active, setActive] = useState(null);
  const current = sectors.find((s) => s.key === active) || sectors[0];
  if (!sectors.length) return null;
  const moduleByKey = Object.fromEntries(modules.map((m) => [m.key, m]));

  return (
    <section className="border-y border-slate-200 bg-white" data-testid="sector-explorer-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <Reveal className="max-w-2xl">
          <Eyebrow>{t("home.sectorsEyebrow")}</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-bold text-marine sm:text-4xl">{t("home.sectorsTitle")}</h2>
          <p className="mt-3 text-slate-500">{t("home.sectorsSub")}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[320px_minmax(0,1fr)]">
          <Reveal className="min-w-0">
            <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0" data-testid="sector-list">
              {sectors.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setActive(s.key)}
                  data-testid={`sector-tab-${s.key}`}
                  className={`flex shrink-0 items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-colors lg:w-full ${
                    current?.key === s.key
                      ? "border-marine bg-marine text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-brand hover:text-brand"
                  }`}
                >
                  <DynIcon name={s.icon} className={`h-5 w-5 ${current?.key === s.key ? "text-gold" : "text-brand"}`} />
                  {lang === "fr" ? s.name_fr : s.name_en}
                </button>
              ))}
            </div>
          </Reveal>
          {current && (
            <Reveal delay={150} className="min-w-0">
              <div className="mg-card h-full p-6 sm:p-8" data-testid="sector-panel">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-marine text-gold">
                    <DynIcon name={current.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-marine">
                    {lang === "fr" ? current.name_fr : current.name_en}
                  </h3>
                </div>
                <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-gold">{t("home.recommended")}</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {(current.recommended_modules || []).map((key) => {
                    const m = moduleByKey[key];
                    if (!m) return null;
                    return (
                      <div key={key} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-mist px-3.5 py-2.5" data-testid={`recommended-module-${key}`}>
                        <DynIcon name={m.icon} className="h-4 w-4 shrink-0 text-brand" />
                        <span className="text-sm font-medium text-marine">{lang === "fr" ? m.name_fr : m.name_en}</span>
                      </div>
                    );
                  })}
                </div>
                <Link to="/secteurs" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-dark" data-testid="sector-view-all">
                  {t("common.viewAll")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useLang();
  const steps = t("home.steps");
  const spaces = t("home.spaces");
  return (
    <section className="bg-marine" data-testid="how-it-works-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Eyebrow light>{t("home.howEyebrow")}</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">{t("home.howTitle")}</h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 100}>
              <div className="relative h-full rounded-xl border border-white/10 bg-white/5 p-6" data-testid={`step-${i}`}>
                <span className="font-display text-4xl font-extrabold text-gold/90">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-lg font-bold text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-16 text-center">
          <h3 className="font-display text-2xl font-bold text-white">{t("home.spacesTitle")}</h3>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {spaces.map((s, i) => (
            <Reveal key={s.title} delay={i * 100}>
              <div className="h-full rounded-xl bg-white p-6" data-testid={`space-${i}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold/15 text-gold">
                  <DynIcon name={s.icon} className="h-5 w-5" />
                </div>
                <h4 className="mt-3 font-display text-base font-bold text-marine">{s.title}</h4>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AiSection() {
  const { t } = useLang();
  const points = t("home.aiPoints");
  return (
    <section className="bg-grid" data-testid="ai-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl bg-marine p-10 text-center">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gold/20 blur-2xl" aria-hidden="true" />
            <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-brand/30 blur-2xl" aria-hidden="true" />
            <Sparkles className="mx-auto h-14 w-14 text-gold" />
            <p className="mt-4 font-display text-2xl font-extrabold text-white">MANAGONE AI</p>
            <p className="mt-2 text-sm text-slate-300">{t("home.aiSub")}</p>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>{t("home.aiEyebrow")}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold text-marine sm:text-4xl">{t("home.aiTitle")}</h2>
          </Reveal>
          <ul className="mt-6 space-y-4">
            {points.map((p, i) => (
              <Reveal key={p} delay={i * 100} as="li">
                <div className="flex items-start gap-3" data-testid={`ai-point-${i}`}>
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <p className="text-sm leading-relaxed text-slate-600">{p}</p>
                </div>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={350}>
            <Link to="/demo" className="btn-gold mt-8" data-testid="ai-cta-button">
              {t("home.aiCta")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <BenefitsBand />
      <Pillars />
      <SectorExplorer />
      <HowItWorks />
      <AiSection />
    </div>
  );
}
